import { memo, useCallback, useEffect, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { useStepperContext } from '@components/stepper';
import type { ProductMinimal } from '@travelity/api';
import StepContent from '@/views/add-product/components/step-content';
import type { PackageFormValues } from '../../add-package.types';
import { AddProductModal } from './components/AddProductModal';
import { UnitDayCard } from './components/UnitDayCard';
import {
    canAddProductByGlobalCurrency,
    getGlobalCurrencyAbbr,
    getStartTimeFieldPaths,
    getUnitProductsStepErrors,
} from './helpers';

export type UnitProductsStepProps = {
    errors: string[];
};

const UnitProductsStep = ({ errors }: UnitProductsStepProps) => {
    const {
        control,
        trigger,
        setValue,
        getValues,
        formState: { errors: formErrors },
    } = useFormContext<PackageFormValues>();
    const { showErrors } = useStepperContext();

    const daysArray = useFieldArray({
        control,
        name: 'products',
    });

    const days = useWatch({
        control,
        name: 'products',
        defaultValue: [],
    });
    const globalCurrencyAbbr = getGlobalCurrencyAbbr(days);
    const [activeDayIndex, setActiveDayIndex] = useState<number | null>(null);

    const handleAddProduct = useCallback(
        (dayIndex: number, product: ProductMinimal) => {
            const existingDays = getValues('products') || [];
            if (!canAddProductByGlobalCurrency(existingDays, product)) return;

            const current = getValues(`products.${dayIndex}.items`) || [];
            const exists = current.some(item => item.id === product.id);
            if (exists) return;

            const nextItems = [...current, product];
            const nextDays = [...existingDays];
            nextDays[dayIndex] = {
                ...nextDays[dayIndex],
                items: nextItems,
            };

            setValue(`products.${dayIndex}.items`, nextItems, {
                shouldDirty: true,
            });
            setValue('globalCurrency', getGlobalCurrencyAbbr(nextDays) ?? '', {
                shouldDirty: true,
            });
        },
        [getValues, setValue]
    );

    const handleRemoveProduct = useCallback(
        (dayIndex: number, productId: string) => {
            const existingDays = getValues('products') || [];
            const current = getValues(`products.${dayIndex}.items`) || [];
            const next = current.filter(item => item.id !== productId);

            const nextDays = [...existingDays];
            nextDays[dayIndex] = {
                ...nextDays[dayIndex],
                items: next,
            };

            setValue(`products.${dayIndex}.items`, next, {
                shouldDirty: true,
            });
            setValue('globalCurrency', getGlobalCurrencyAbbr(nextDays) ?? '', {
                shouldDirty: true,
            });
        },
        [getValues, setValue]
    );

    const handleRemoveDay = useCallback(
        (dayIndex: number) => {
            const existingDays = getValues('products') || [];
            const nextDays = existingDays.filter((_, idx) => idx !== dayIndex);

            daysArray.remove(dayIndex);
            setValue('globalCurrency', getGlobalCurrencyAbbr(nextDays) ?? '', {
                shouldDirty: true,
            });
        },
        [daysArray, getValues, setValue]
    );

    const handleOpenModal = useCallback((dayIndex: number) => {
        setActiveDayIndex(dayIndex);
    }, []);

    const handleCloseModal = useCallback(() => {
        setActiveDayIndex(null);
    }, []);

    const stepErrors = getUnitProductsStepErrors(days, errors);

    useEffect(() => {
        const startTimeFields = getStartTimeFieldPaths(days);
        if (startTimeFields.length > 0) {
            trigger(startTimeFields as any);
        }
    }, [days, trigger]);

    return (
        <StepContent
            title="Unit Products"
            errors={stepErrors}
            doNotDisable={false}
        >
            <>
                <Stack spacing={3}>
                    <Button
                        variant="outlined"
                        onClick={() =>
                            daysArray.append({
                                day_index:
                                    (days[days.length - 1]?.day_index ?? 0) + 1,
                                items: [],
                            })
                        }
                        sx={{ width: 'fit-content', alignSelf: 'flex-end' }}
                    >
                        Add day
                    </Button>

                    {daysArray.fields.map((field, index) => {
                        const day = days[index] || {
                            day_index: index + 1,
                            items: [],
                        };

                        return (
                            <UnitDayCard
                                key={field.id}
                                dayIndex={index}
                                day={day}
                                prevDayIndex={days[index - 1]?.day_index}
                                showErrors={showErrors}
                                dayIndexError={
                                    formErrors.products?.[index]?.day_index
                                        ?.message as string | undefined
                                }
                                onChangeDayIndex={(dayIndex, value) =>
                                    setValue(
                                        `products.${dayIndex}.day_index`,
                                        value,
                                        { shouldDirty: true }
                                    )
                                }
                                onOpenAddProduct={handleOpenModal}
                                onRemoveDay={handleRemoveDay}
                                onRemoveProduct={handleRemoveProduct}
                            />
                        );
                    })}
                </Stack>
                <AddProductModal
                    open={activeDayIndex !== null}
                    dayLabel={
                        activeDayIndex === null
                            ? ''
                            : `Day ${
                                  days[activeDayIndex]?.day_index ??
                                  activeDayIndex + 1
                              }`
                    }
                    selectedProducts={
                        activeDayIndex === null
                            ? []
                            : days[activeDayIndex]?.items || []
                    }
                    globalCurrencyAbbr={globalCurrencyAbbr}
                    onAdd={product => {
                        if (activeDayIndex === null) return;
                        handleAddProduct(activeDayIndex, product);
                    }}
                    onClose={handleCloseModal}
                />
            </>
        </StepContent>
    );
};

export default memo(UnitProductsStep);
